import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {ECaseSnackBarService, ECaseUtils, ExplorerService} from 'synto-common';
import {DocumentItem} from '../document-list.models';
import {
  isUnknownRequestOutcome,
  OUTCOME_UNKNOWN,
  OutcomeMessage,
  replaceOutcome
} from '../utils/document-approval.utils';

export interface DocumentReplaceDialogData {
  row: DocumentItem;
  screenName: string;
}

interface ReplaceRequestError {
  error?: unknown;
  errorMessage?: string;
  message?: string;
  status?: number;
}

interface ReplaceDocumentResult {
  replaced: boolean;
  approvalWorkflow: string;
}

@Component({
  selector: 'app-document-replace-dialog',
  templateUrl: './document-replace-dialog.component.html',
  styleUrls: ['./document-replace-dialog.component.scss']
})
export class DocumentReplaceDialogComponent {

  uploadedFile: File | undefined;
  submitting = false;

  constructor(
    public dialogRef: MatDialogRef<DocumentReplaceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentReplaceDialogData,
    private translate: TranslateService,
    private explorerService: ExplorerService,
    private eCaseSnackBarService: ECaseSnackBarService
  ) {
  }

  uploadFile($event: Event): void {
    const input = $event.target as HTMLInputElement;
    this.uploadedFile = input.files?.[0];
  }

  removeFile(): void {
    this.uploadedFile = undefined;
  }

  onConfirm(): void {
    if (!this.uploadedFile || !this.data?.row || this.submitting) {
      return;
    }
    const edmRepositoryDocumentId = this.data.row?.edmRepositoryDocumentId;
    if (!edmRepositoryDocumentId) {
      this.eCaseSnackBarService.show('failure',
        ECaseUtils.getTranslatedValueFromKey(this.translate, 'ecase.explorer.documentList.replace.documentIdMissing'));
      return;
    }
    this.submitting = true;
    this.dialogRef.disableClose = true;
    const formData = new FormData();
    formData.append('file', this.uploadedFile);
    this.explorerService.replaceDocument(edmRepositoryDocumentId, formData, this.data.screenName).subscribe({
      next: (result: ReplaceDocumentResult) => this.handleResponse(result),
      error: (error: unknown) => this.handleRequestError(error)
    });
  }

  onClose(): void {
    if (!this.submitting) {
      this.dialogRef.close(false);
    }
  }

  private handleResponse(response: unknown): void {
    const result = this.asStructuredResult(response);
    if (!result) {
      this.handleUnknownOutcome();
      return;
    }

    this.handleResult(result);
  }

  private handleResult(result: ReplaceDocumentResult): void {
    this.finishSubmission();
    this.showOutcomeMessage(replaceOutcome(result.approvalWorkflow, result.replaced));
    if (result.replaced) {
      this.dialogRef.close(true);
    }
  }

  private handleRequestError(error: unknown): void {
    const result = this.getStructuredResult(error);
    if (result) {
      this.handleResult(result);
      return;
    }

    if (isUnknownRequestOutcome(error)) {
      this.handleUnknownOutcome();
      return;
    }

    this.finishSubmission();
    const requestError = error as ReplaceRequestError;
    if (requestError?.status === 413) {
      this.eCaseSnackBarService.show('failure',
        ECaseUtils.getTranslatedValueFromKey(this.translate, 'ecase.explorer.documentList.replace.fileTooLarge'));
      return;
    }

    const errorBody = requestError?.error as {errorMessage?: string; message?: string} | undefined;
    const errorMessage = requestError?.errorMessage || errorBody?.errorMessage || errorBody?.message || requestError?.message;
    this.eCaseSnackBarService.show('failure',
      ECaseUtils.getTranslatedValueFromKey(this.translate, errorMessage || 'ecase.explorer.documentList.replace.error'));
  }

  private getStructuredResult(error: unknown): ReplaceDocumentResult | undefined {
    return this.asStructuredResult((error as ReplaceRequestError)?.error);
  }

  private asStructuredResult(responseBody: unknown): ReplaceDocumentResult | undefined {
    if (!responseBody || typeof responseBody !== 'object') {
      return undefined;
    }
    const candidate = responseBody as Partial<ReplaceDocumentResult>;
    return typeof candidate.replaced === 'boolean' && typeof candidate.approvalWorkflow === 'string' ?
      candidate as ReplaceDocumentResult : undefined;
  }

  private finishSubmission(): void {
    this.submitting = false;
    this.dialogRef.disableClose = false;
  }

  private handleUnknownOutcome(): void {
    this.finishSubmission();
    this.showOutcomeMessage(OUTCOME_UNKNOWN);
    this.dialogRef.close(true);
  }

  private showOutcomeMessage(message: OutcomeMessage): void {
    this.eCaseSnackBarService.show(message.status,
      ECaseUtils.getTranslatedValueFromKey(this.translate, message.key));
  }

}
